import { createApi } from '@reduxjs/toolkit/query/react';
import { useMemo } from 'react';

import { AUTH_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

const TagType = {
    USER_PROFILE: 'USER_PROFILE',
    COMPASS_USER_NAME_MAP: 'COMPASS_USER_NAME_MAP',
} as const;

export interface PayerUserMapEntry {
    full_name: string;
    has_pbm_only_access?: boolean;
}

export const authApi = createApi({
    baseQuery: getBaseQuery(AUTH_API_URL),
    reducerPath: 'authApi',
    tagTypes: Object.values(TagType),
    endpoints: (build) => ({
        getUserProfile: build.query<
            {
                fullName: string;
                email: string;
                role: string;
                segmentIds: string[];
            },
            void
        >({
            query: () => 'profile/compass',
            providesTags: [TagType.USER_PROFILE],
            transformResponse: (response: {
                upn: string;
                full_name: string;
                display_name: string;
                segment_ids: string[];
            }) => ({
                fullName: response?.full_name,
                email: response?.upn,
                role: response?.display_name,
                segmentIds: response?.segment_ids,
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving user profile.',
            ),
        }),
        putUserProfile: build.mutation<void, { fullName: string }>({
            query: ({ fullName }) => ({
                url: 'profile/compass',
                method: 'PUT',
                body: { full_name: fullName },
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred updating user profile.'),
            invalidatesTags: (_, error) =>
                !error ? [TagType.USER_PROFILE, TagType.COMPASS_USER_NAME_MAP] : [],
        }),
        getCompassUserNameMap: build.query<Record<string, string>, void>({
            query: () => 'profile/compass/name_map',
            providesTags: [TagType.COMPASS_USER_NAME_MAP],
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving user names.'),
        }),
        getPayerUserNameMap: build.query<Record<string, string>, { payerId?: string }>({
            query: ({ payerId }) => `profile/payer/name_map/${payerId}`,
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving user names.'),
        }),
        getPayerUserPbmAccessMap: build.query<Record<string, boolean>, { payerId: string }>({
            query: ({ payerId }) => ({
                url: `profile/payer/name_map/${payerId}`,
                params: { include_pbm_only_access: true },
            }),
            transformResponse: (response: Record<string, PayerUserMapEntry>) =>
                Object.fromEntries(
                    Object.entries(response).map(([upn, entry]) => [
                        upn,
                        !!entry.has_pbm_only_access,
                    ]),
                ),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving PBM access map.',
            ),
        }),
        getPayerUserWithPermNameMap: build.query<
            Record<string, PayerUserMapEntry>,
            { segmentId: string; permission: string }
        >({
            query: ({ segmentId, permission }) => ({
                url: 'payer_users/map',
                params: {
                    segment_id: segmentId,
                    permission,
                    include_pbm_only_access: true,
                },
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving user names.'),
        }),
    }),
});

export const {
    useGetUserProfileQuery,
    usePutUserProfileMutation,
    useGetCompassUserNameMapQuery,
    useGetPayerUserNameMapQuery,
    useGetPayerUserPbmAccessMapQuery,
    useGetPayerUserWithPermNameMapQuery,
    useLazyGetPayerUserWithPermNameMapQuery,
} = authApi;

export const useUserNameMap = (payerId?: string) => {
    const { data: compassUserNameMap } = useGetCompassUserNameMapQuery();
    const { data: payerUserNameMap } = useGetPayerUserNameMapQuery({ payerId }, { skip: !payerId });
    return useMemo(
        () => ({ ...compassUserNameMap, ...payerUserNameMap }),
        [compassUserNameMap, payerUserNameMap],
    );
};

export const usePayerUserPbmAccessMap = (payerId?: string) => {
    const { data: pbmAccessByUpn } = useGetPayerUserPbmAccessMapQuery(
        { payerId: payerId! },
        { skip: !payerId },
    );
    return pbmAccessByUpn ?? {};
};
